import { defineStore } from 'pinia';
import * as configcat from '@configcat/sdk/browser';
import { FeatureFlagDefaults } from '@/constants/FeatureFlags';

type FlagMap = Record<string, boolean>;

export interface FeatureFlagIdentity {
	identifier: string;
	role?: string;
	brokerId?: string;
}

/**
 * Intervalo do auto-poll. É o que faz o interruptor valer sem deploy: uma aba já aberta pega a
 * mudança feita no painel do ConfigCat em até um minuto, sem recarregar.
 */
const POLL_INTERVAL_SECONDS = 60;

const SDK_KEY = import.meta.env.VITE_CONFIGCAT_SDK_KEY as string | undefined;

/**
 * Cliente fora do store, e não no state: é um objeto com timers e assinatura de eventos, que o
 * Pinia não deve tornar reativo.
 */
let client: configcat.IConfigCatClient | null = null;
let currentUser: configcat.IUser | undefined;

export const FeatureFlagStore = defineStore('featureFlag', {
	state: () => ({
		flags: { ...FeatureFlagDefaults } as FlagMap,
		ready: false,
	}),

	actions: {
		/**
		 * Cria o cliente e dispara a primeira avaliação.
		 *
		 * NÃO RETORNA NADA QUE DEVA SER AGUARDADO, e isso é o ponto central do desenho. A montagem
		 * anterior (Flagsmith) fazia `await ffStore.init()` antes de registrar o onAuthStateChanged,
		 * e como o App.tsx só sai do spinner dentro daquele callback, todo boot pagava até 2,5s de
		 * tela de loading antes de a tela de login aparecer. O histórico do arquivo guarda o
		 * resultado: "hotfix: ffStore travando app" e, oito minutos depois, "fix: timeout".
		 *
		 * Se alguém for "consertar" isto com um await, o bug volta inteiro.
		 */
		init() {
			// Sem chave configurada o cliente não nasce: desenvolvimento local e testes rodam com os
			// defaults, sem rede e sem erro no console.
			if (client || !SDK_KEY) {
				return;
			}

			client = configcat.getClient(SDK_KEY, configcat.PollingMode.AutoPoll, {
				pollIntervalSeconds: POLL_INTERVAL_SECONDS,
				setupHooks: (hooks) =>
					hooks
						.on('clientReady', () => void this.refresh())
						.on('configChanged', () => void this.refresh()),
			});

			void this.refresh();
		},

		/**
		 * Reavalia as flags com o contexto do usuário, para permitir segmentação por imobiliária ou
		 * papel no painel. Dispara e esquece: o login não espera por isto.
		 */
		identify(identity: FeatureFlagIdentity) {
			currentUser = {
				identifier: identity.identifier,
				custom: {
					role: identity.role ?? '',
					brokerId: identity.brokerId ?? '',
				},
			};

			void this.refresh();
		},

		/** Volta à avaliação anônima. Chamado no logout. */
		reset() {
			currentUser = undefined;
			void this.refresh();
		},

		/**
		 * Lê o ConfigCat e atualiza o snapshot reativo.
		 *
		 * O componente precisa de leitura síncrona, e getValueAsync é assíncrono — daí o snapshot.
		 * A tela renderiza com o default e se corrige quando a resposta chega; como o SDK de browser
		 * guarda a última config em localStorage, só um primeiro acesso absoluto vê o default.
		 */
		async refresh() {
			if (!client) {
				return;
			}

			const keys = Object.keys(FeatureFlagDefaults);

			const values = await Promise.all(
				keys.map(async (key): Promise<boolean> => {
					// `?? true` fecha o acesso por índice e repete o fail-open: uma chave sem default
					// declarado não deve esconder tela.
					const fallback = FeatureFlagDefaults[key] ?? true;

					try {
						// Genérico explícito: a assinatura do SDK devolve string | number | boolean, e
						// deixar inferir alargaria o tipo do snapshot.
						return await client!.getValueAsync<boolean>(key, fallback, currentUser);
					} catch (error) {
						// Uma flag que falha não pode derrubar as outras nem a tela.
						console.error(`[FeatureFlags] Failed to evaluate "${key}":`, error);
						return fallback;
					}
				}),
			);

			this.flags = keys.reduce<FlagMap>((acc, key, index) => {
				acc[key] = values[index] ?? true;
				return acc;
			}, {});

			this.ready = true;
		},

		/**
		 * Leitura SÍNCRONA do snapshot. Nunca aguarda, nunca vai à rede.
		 *
		 * Chave desconhecida cai no default declarado em FeatureFlagDefaults e, na falta dele, em
		 * `true` — coerente com o fail-open: uma flag que ninguém declarou não deve esconder tela.
		 */
		isEnabled(key: string): boolean {
			return this.flags[key] ?? FeatureFlagDefaults[key] ?? true;
		},
	},
});

export default FeatureFlagStore;
